<template>
  <div
    :class="
      $twMerge(
        'relative z-10 px-4 py-5 md:px-8 md:py-6 text-center shadow-[3px_23px_52.9px_-2px_#0000001A] max-w-max mx-auto border border-[#cccccc]/60 dark:border-[#624574]/50 bg-[image:var(--bg-circle-highlight)] backdrop-blur-sm rounded-xl ',
        props.appendClass,
      )
    "
  >
    <h2 class="font-semibold text-xl mb-6" v-if="props.title.length">
      {{ props.title }}
    </h2>

    <div
      :class="
        $twMerge(
          'flex flex-wrap justify-start items-center gap-8 md:gap-x-20 md:gap-y-10 ',
          props.partnersAppendClass,
        )
      "
    >
      <NuxtLink
        :to="partner.link"
        target="_blank"
        class="flex items-center"
        v-for="partner in resolvedPartners"
      >
        <NuxtImg
          :src="partner.img"
          :alt="partner.alt"
          class="mr-2 partner-logo-light"
          :width="partner.width"
          :height="partner.height"
          :loading="eager ? 'eager' : 'lazy'"
          decoding="async"
          format="webp"
        />
        <NuxtImg
          :src="partner.darkImg"
          :alt="partner.alt"
          class="mr-2 partner-logo-dark"
          :width="partner.width"
          :height="partner.height"
          :loading="eager ? 'eager' : 'lazy'"
          decoding="async"
          format="webp"
        />
        <span class="text-xl">{{ partner.name }}</span>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
const { isMobile } = useDevice();
const { $twMerge } = useNuxtApp();

const props = defineProps({
  appendClass: {
    type: String,
    default: "",
  },
  // Default lazy: this component sits below the fold on about-us/contact-us.
  // The homepage hero passes eager=true since it is above the fold there.
  eager: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  partnersAppendClass: {
    type: String,
    default: "",
  },
  partners: {
    type: Array,
    // Dimensions carry both desktop (width/height) and mobile
    // (mobileWidth/mobileHeight). defineProps() defaults are hoisted above the
    // local `isMobile`, so the active size is resolved in `resolvedPartners`.
    default: () => [
      {
        name: "Laravel",
        width: "47",
        height: "49",
        mobileWidth: "38",
        mobileHeight: "40",
        img: "/images/icons/Laravel.webp",
        darkImg: "/images/laravelDarkimage.webp",
        alt: "Laravel Icon",
        link: "https://laravel.com/partners?region=asia",
      },
      {
        name: "Nuxt",
        width: "59",
        height: "39",
        mobileWidth: "37",
        mobileHeight: "23",
        img: "/images/icons/nuxt.webp",
        darkImg: "/images/nuxtDarkImage.webp",
        alt: "Nuxt icon",
        link: "https://nuxt.com/enterprise/agencies/webreinvent",
      },
      {
        name: "Flutter",
        width: "45",
        height: "45",
        mobileWidth: "23",
        mobileHeight: "31",
        img: "/images/icons/flutter.webp",
        darkImg: "/images/icons/flutter-dark.webp",
        alt: "Flutter Icon",
        link: "https://flutter.dev/consultants?locations=Asia/",
      },
      {
        name: "Vue",
        width: "49",
        height: "42",
        mobileWidth: "37",
        mobileHeight: "30",
        img: "/images/icons/Vue.webp",
        darkImg: "/images/vueDarkImage.webp",
        alt: "Vue icon",
        link: "https://vuejs.org/partners/webreinvent.html",
      },
      {
        name: "Builder.io",
        width: "32",
        height: "36",
        mobileWidth: "22",
        mobileHeight: "26",
        img: "/images/icons/Builder.webp",
        darkImg: "/images/BuilderDarkImage.webp",
        alt: "Builder.io icon",
        link: "https://www.builder.io/m/partners/webreinvent",
      },
      {
        name: "Phrase",
        width: "33",
        height: "48",
        mobileWidth: "23",
        mobileHeight: "38",
        img: "/images/icons/Phrase.webp",
        darkImg: "/images/icons/phrase-dark.webp",
        alt: "Phrase IO Alt Icon",
        link: "https://phrase.com/partners/",
      },
    ],
  },
});

// Resolve the active dimensions for the current device. Callers that pass
// already-resolved `width`/`height` (no mobile* fields) fall back to those.
const resolvedPartners = computed(() =>
  props.partners.map((partner) => ({
    ...partner,
    width: isMobile ? partner.mobileWidth ?? partner.width : partner.width,
    height: isMobile ? partner.mobileHeight ?? partner.height : partner.height,
  })),
);
</script>

<style>
/* Partner logos: the colored logo shows in light mode, the white logo in dark mode.
   Driven by the theme class on <html> (not a JS color-mode check) so it follows the
   real theme even after hydration. */
.partner-logo-dark {
  display: none;
}
.my-app-dark .partner-logo-light {
  display: none;
}
.my-app-dark .partner-logo-dark {
  display: block;
}
</style>
